/**
 * Predictive Search - Fullscreen Live Search
 * SeKa Industrial Theme
 */

class PredictiveSearch {
  constructor() {
    this.overlay = document.getElementById('predictive-search-overlay');
    this.input = document.getElementById('predictive-search-input');
    this.results = document.getElementById('predictive-search-results');
    this.viewAllLink = document.getElementById('predictive-search-view-all');
    this.loading = document.getElementById('predictive-search-loading');
    this.noResults = document.getElementById('predictive-search-no-results');
    this.querySpan = document.getElementById('predictive-search-query');
    this.closeBtn = document.getElementById('predictive-search-close');
    this.searchTriggers = document.querySelectorAll('[data-predictive-search-open]');
    
    this.debounceTimer = null;
    this.abortController = null;
    
    this.bindEvents();
  }
  
  bindEvents() {
    // Open search overlay
    this.searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
    
    // Close search overlay
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    
    // Input handling with debounce
    this.input?.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.search(), 300);
    });
    
    // Enter key to submit search
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submitSearch();
      }
    });
  }
  
  submitSearch() {
    const query = this.input.value.trim();
    if (query.length > 0) {
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  }
  
  open() {
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input.focus(), 100);
  }
  
  close() {
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.clearResults();
  }
  
  isOpen() {
    return this.overlay.classList.contains('is-open');
  }
  
  clearResults() {
    this.input.value = '';
    this.results.innerHTML = '';
    this.hideViewAll();
    this.hideLoading();
    this.hideNoResults();
  }
  
  showViewAll(query) {
    if (this.viewAllLink) {
      this.viewAllLink.href = `/search?q=${encodeURIComponent(query)}`;
      this.viewAllLink.style.display = 'flex';
    }
  }
  
  hideViewAll() {
    if (this.viewAllLink) {
      this.viewAllLink.style.display = 'none';
    }
  }
  
  showLoading() {
    this.loading.style.display = 'flex';
  }
  
  hideLoading() {
    this.loading.style.display = 'none';
  }
  
  showNoResults(query) {
    this.querySpan.textContent = query;
    this.noResults.style.display = 'block';
  }
  
  hideNoResults() {
    this.noResults.style.display = 'none';
  }
  
  async search() {
    const query = this.input.value.trim();
    
    if (query.length < 2) {
      this.results.innerHTML = '';
      this.hideNoResults();
      return;
    }
    
    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    
    this.showLoading();
    this.hideNoResults();
    
    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide`,
        { signal: this.abortController.signal }
      );
      const data = await response.json();
      
      this.hideLoading();
      this.renderResults(data.resources.results.products, query);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.hideLoading();
      }
    }
  }
  
  renderResults(products, query) {
    if (!products || products.length === 0) {
      this.results.innerHTML = '';
      this.hideViewAll();
      this.showNoResults(query);
      return;
    }
    
    this.hideNoResults();
    
    const html = `
      <div class="predictive-search__products">
        ${products.map(product => this.renderProduct(product)).join('')}
      </div>
    `;
    
    this.results.innerHTML = html;
    this.showViewAll(query);
  }
  
  renderProduct(product) {
    const price = parseFloat(product.price) || 0;
    const isRequestOnly = price === 0;
    
    // Preis-Anzeige nur wenn Preis > 0
    let priceHtml = '';
    if (isRequestOnly) {
      priceHtml = `<span class="predictive-search__request-badge">Preis auf Anfrage</span>`;
    } else {
      const formattedPrice = this.formatMoney(product.price);
      const comparePrice = product.compare_at_price_max > product.price 
        ? this.formatMoney(product.compare_at_price_max) 
        : null;
      priceHtml = `
        <span class="predictive-search__price">${formattedPrice}</span>
        ${comparePrice ? `<span class="predictive-search__compare-price">${comparePrice}</span>` : ''}
      `;
    }

    return `
      <a href="${product.url}" class="predictive-search__product">
        <div class="predictive-search__product-image">
          ${product.image 
            ? `<img src="${product.image.replace(/(\.[^.]+)$/, '_200x200$1')}" alt="${product.title}" loading="lazy">`
            : `<div class="predictive-search__product-placeholder"></div>`
          }
        </div>
        <div class="predictive-search__product-info">
          <span class="predictive-search__product-title">${product.title}</span>
          <div class="predictive-search__product-price">
            ${priceHtml}
          </div>
        </div>
      </a>
    `;
  }
  
  formatMoney(priceString) {
    // Shopify API liefert Preise als String (z.B. "360.00" für 360€)
    // Nicht als Cents, sondern als volle Euro-Beträge mit Dezimalstellen
    const price = parseFloat(priceString) || 0;
    
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearch();
});